/**
 * AuthLayout - Layout fuer Authentifizierungs-Seiten
 *
 * Zentrierter Content mit optionalem Branding-Bereich.
 * Ideal fuer Login, Register, Password-Reset, Email-Verification.
 *
 * WICHTIG: Kein overflow - verlässt sich auf AppLayout.main als Scroll-Container.
 *
 * @example
 * ```tsx
 * // Einfache Verwendung
 * <AuthLayout>
 *   <LoginForm />
 * </AuthLayout>
 *
 * // Mit Branding
 * <AuthLayout
 *   showBranding
 *   brandingTitle="Willkommen zurueck"
 *   brandingSubtitle="Melde dich an, um fortzufahren"
 * >
 *   <LoginForm />
 * </AuthLayout>
 * ```
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';
import { VStack, HStack } from '@/components/core';
import { defaultBrandingTokens } from '@/design-system/tokens/branding';

export interface AuthLayoutProps {
  /**
   * Der Hauptinhalt (z.B. Login-Form)
   */
  children: ReactNode;

  /**
   * Ob der Branding-Bereich angezeigt werden soll
   * @default true
   */
  showBranding?: boolean;

  /**
   * Titel im Branding-Bereich
   */
  brandingTitle?: string;

  /**
   * Untertitel im Branding-Bereich
   */
  brandingSubtitle?: string;

  /**
   * Maximale Breite der Content-Box
   * @default 'sm'
   */
  maxWidth?: 'xs' | 'sm' | 'md';

  /**
   * Zusaetzliche CSS-Klasse fuer den Container
   */
  className?: string;

  /**
   * Zusaetzliche CSS-Klasse fuer die Content-Box
   */
  contentClassName?: string;
}

// Max-Width Mapping
const maxWidthClasses: Record<NonNullable<AuthLayoutProps['maxWidth']>, string> = {
  xs: 'max-w-xs',   // 320px
  sm: 'max-w-sm',   // 384px
  md: 'max-w-md',   // 448px
};

export function AuthLayout({
  children,
  showBranding = true,
  brandingTitle,
  brandingSubtitle,
  maxWidth = 'sm',
  className,
  contentClassName,
}: AuthLayoutProps) {
  const { config } = useDesign();
  const siteTitle = config?.branding?.site_title || defaultBrandingTokens.siteName;
  const logoUrl = config?.branding?.logo_url;

  return (
    <VStack
      align="center"
      justify="center"
      className={cn(
        'auth-layout w-full min-h-full',
        // Padding
        'px-token-md py-token-xl sm:py-token-3xl',
        // WICHTIG: Kein overflow
        'overflow-visible',
        className
      )}
    >
      {/* Branding Section */}
      {showBranding && (
        <div className="auth-layout-branding text-center mb-token-xl">
          {/* Logo */}
          <Link
            to="/"
            className="inline-flex items-center justify-center mb-token-md transition-opacity hover:opacity-80"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={siteTitle}
                className="h-16 w-auto rounded-token-card"
              />
            ) : (
              <div
                className="w-16 h-16 rounded-token-card flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--color-primary-gradient-start), var(--color-accent-text))',
                }}
              >
                <Heart size={32} className="text-white" />
              </div>
            )}
          </Link>

          {/* Titel */}
          {brandingTitle && (
            <h1
              className="text-2xl sm:text-3xl font-bold mb-token-xs"
              style={{ color: 'var(--color-primary-text)' }}
            >
              {brandingTitle}
            </h1>
          )}

          {/* Untertitel */}
          {brandingSubtitle && (
            <p className="text-sm sm:text-base text-muted-foreground">
              {brandingSubtitle}
            </p>
          )}
        </div>
      )}

      {/* Content Box */}
      <div
        className={cn(
          'auth-layout-content w-full',
          maxWidthClasses[maxWidth],
          contentClassName
        )}
      >
        {children}
      </div>
    </VStack>
  );
}

export default AuthLayout;
